import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { useLazyQuery, useMutation, gql } from "@apollo/client";

const SEARCH_USERS = gql`
  query SearchUsers($query: String!) {
    searchUsers(query: $query) {
      id
      username
      email
      firstName
      lastName
      avatarUrl
    }
  }
`;

const SEND_FRIEND_REQUEST = gql`
  mutation SendFriendRequest($toUserId: ID!) {
    sendFriendRequest(toUserId: $toUserId) {
      id
      status
    }
  }
`;

const REMOVE_FRIEND = gql`
  mutation RemoveFriend($userId: ID!) {
    removeFriend(userId: $userId) {
      id
    }
  }
`;

export default function UserSearchDialog({
  open,
  onClose,
  friends = [],
  currentUserId,
}) {
  const [query, setQuery] = useState("");
  const [searchUsers, { data, loading, error }] = useLazyQuery(SEARCH_USERS);
  const [sendFriendRequest] = useMutation(SEND_FRIEND_REQUEST);
  const [removeFriend] = useMutation(REMOVE_FRIEND);

  const friendIds = new Set(friends.map((f) => f.id));

  const handleSearch = () => {
    if (query.trim()) {
      searchUsers({ variables: { query: query.trim() } });
    }
  };

  const handleSendRequest = async (userId) => {
    try {
      await sendFriendRequest({ variables: { toUserId: userId } });
      alert("Friend request sent!");
    } catch (e) {
      alert(e.message);
    }
  };

  const handleRemoveFriend = async (userId) => {
    try {
      await removeFriend({ variables: { userId } });
      alert("Friend removed.");
    } catch (e) {
      alert(e.message);
    }
  };

  const filteredUsers =
    data?.searchUsers.filter((u) => u.id !== currentUserId) || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>User Search</DialogTitle>
      <DialogContent>
        <TextField
          label="Search by email, name, or username"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          sx={{ mb: 2 }}
        />
        <Button onClick={handleSearch} variant="contained" disabled={loading}>
          Search
        </Button>

        {loading && <p>Searching…</p>}
        {error && <p style={{ color: "red" }}>{error.message}</p>}

        {filteredUsers.length > 0 ? (
          <List>
            {filteredUsers.map((user) => (
              <ListItem key={user.id}>
                <ListItemAvatar>
                  <Avatar src={user.avatarUrl ?? undefined}>
                    {user.username[0]?.toUpperCase() || "U"}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${user.username} (${user.firstName || ""} ${
                    user.lastName || ""
                  })`}
                  secondary={user.email}
                />
                <ListItemSecondaryAction>
                  {friendIds.has(user.id) ? (
                    <Tooltip title="Unfriend">
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => handleRemoveFriend(user.id)}
                      >
                        <PersonRemoveIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Send Friend Request">
                      <IconButton
                        edge="end"
                        color="primary"
                        onClick={() => handleSendRequest(user.id)}
                      >
                        <PersonAddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          !loading && <p>No users found.</p>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
